import React from 'react';
import { Layout } from '../components/Layout';

const Wrap = ({ title, children, testid }) => (
  <Layout>
    <div className="mx-auto max-w-3xl px-4 sm:px-6 md:px-10 py-12" data-testid={testid}>
      <div className="label-mono mb-3">Legal</div>
      <h1 className="text-3xl md:text-4xl font-medium tracking-tight">{title}</h1>
      <div className="prose prose-sm dark:prose-invert mt-8 max-w-none text-muted-foreground space-y-4 leading-relaxed">{children}</div>
    </div>
  </Layout>
);

export const RefundPolicy = () => (
  <Wrap title="Refund Policy" testid="refund-page">
    <p><b>Last updated:</b> June 2026</p>
    <p>PackCheck is a paid SaaS service. Each plan purchase grants compliance-audit credits that are consumed upon use. We aim to be fair and transparent with refunds.</p>
    <h2 className="text-foreground">Eligibility</h2>
    <ul className="list-disc pl-5">
      <li>Pay-per-check (₹750): refundable in full only if the credit has not been consumed AND request is made within 7 days of purchase.</li>
      <li>Monthly subscription (₹4,500/month): cancel anytime to stop future billing. The current month is non-refundable once any credit has been used.</li>
      <li>Annual bundle (₹22,000): refundable on a pro-rata basis for unused credits within the first 30 days. Beyond 30 days, refunds are at our discretion.</li>
      <li>Enterprise: governed by your master service agreement.</li>
    </ul>
    <h2 className="text-foreground">How to request a refund</h2>
    <p>Email <a href="mailto:billing@thereforedesign.co.in" className="underline">billing@thereforedesign.co.in</a> from the email associated with your PackCheck account, stating your order ID and reason. We respond within 3 business days.</p>
    <h2 className="text-foreground">Processing time</h2>
    <p>Approved refunds are credited to the original payment method within 7–10 business days, less any payment-gateway fees.</p>
    <h2 className="text-foreground">No refund situations</h2>
    <p>Credits already consumed; accounts terminated for terms violation; chargebacks initiated without contacting us first.</p>
  </Wrap>
);

export const Terms = () => (
  <Wrap title="Terms & Conditions" testid="terms-page">
    <p><b>Last updated:</b> June 2026</p>
    <p>By accessing PackCheck ("Service") operated by Therefore Design Studio Pvt Ltd, you agree to be bound by these terms.</p>
    <h2 className="text-foreground">1. Service Description</h2>
    <p>PackCheck is an AI-assisted FSSAI &amp; Legal Metrology artwork-compliance auditor. Audit findings are <b>indicative only</b> and do not constitute legal advice or compliance certification. Users are responsible for verifying compliance independently before printing.</p>
    <h2 className="text-foreground">2. Account</h2>
    <p>You must provide accurate information. You are responsible for safeguarding your account credentials and all activity under your account.</p>
    <h2 className="text-foreground">3. Payment &amp; Credits</h2>
    <p>Plans are billed in INR via Razorpay. Credits are non-transferable. Monthly subscriptions auto-renew until cancelled.</p>
    <h2 className="text-foreground">4. Acceptable Use</h2>
    <p>Do not upload content you don't own rights to, attempt to reverse-engineer the service, or use it to mislead consumers.</p>
    <h2 className="text-foreground">5. Intellectual Property</h2>
    <p>You retain ownership of artwork you upload. We retain ownership of the platform, regulation database, and audit methodology. Aggregated, anonymised audit metrics may be used to improve the service.</p>
    <h2 className="text-foreground">6. Limitation of Liability</h2>
    <p>To the maximum extent permitted by law, our total liability is limited to the amount you paid in the 12 months preceding any claim. We are not liable for indirect or consequential damages.</p>
    <h2 className="text-foreground">7. Governing law</h2>
    <p>Bangalore, Karnataka. Disputes subject to Bangalore courts' exclusive jurisdiction.</p>
    <h2 className="text-foreground">8. Contact</h2>
    <p><a href="mailto:legal@thereforedesign.co.in" className="underline">legal@thereforedesign.co.in</a></p>
  </Wrap>
);

export const Privacy = () => (
  <Wrap title="Privacy Policy" testid="privacy-page">
    <p><b>Last updated:</b> June 2026</p>
    <h2 className="text-foreground">Data we collect</h2>
    <ul className="list-disc pl-5">
      <li>Account: name, email, profile picture (via Google OAuth).</li>
      <li>Audit data: uploaded artwork PDFs, extracted text, audit results.</li>
      <li>Usage analytics: pages visited, audit timestamps, IP address (anonymised after 30 days).</li>
      <li>Payment metadata: order ID, plan, amount (full card details never touch our servers — handled by Razorpay).</li>
    </ul>
    <h2 className="text-foreground">How we use it</h2>
    <ul className="list-disc pl-5">
      <li>To run audits and store reports.</li>
      <li>To send transactional emails (audit-ready, receipt) via your connected Gmail.</li>
      <li>To improve regulation rules using aggregated, anonymised insights.</li>
    </ul>
    <h2 className="text-foreground">Sharing</h2>
    <p>We do not sell personal data. Sub-processors: Anthropic (LLM via Emergent), Razorpay (payments), Google Cloud (sign-in &amp; Gmail send). All bound by appropriate DPAs.</p>
    <h2 className="text-foreground">Retention</h2>
    <p>Audits stored until you delete them. Account data retained for 12 months after account deletion, then purged.</p>
    <h2 className="text-foreground">Your rights</h2>
    <p>Access, correction, deletion, portability. Delete your account anytime via <b>Account → Danger zone</b>. Or email <a href="mailto:privacy@thereforedesign.co.in" className="underline">privacy@thereforedesign.co.in</a>.</p>
  </Wrap>
);
